/**
 * useAnalysis Hook
 *
 * Manages AI analysis state for ServiceNow records
 */

import { useState, useCallback } from 'react';
import { fetchAnalysis, type RecordContext, type AnalysisResult, type ApiError } from '@/services/api';

interface UseAnalysisResult {
  analysis: AnalysisResult | null;
  loading: boolean;
  error: string | null;
  analyze: (record: RecordContext) => Promise<void>;
  reset: () => void;
}

export function useAnalysis(): UseAnalysisResult {
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Trigger AI analysis for a record
   */
  const analyze = useCallback(async (record: RecordContext) => {
    setLoading(true);
    setError(null);

    try {
      console.log(`[useAnalysis] Analyzing ${record.number}`);
      const result = await fetchAnalysis(record);
      setAnalysis(result);
      console.log('[useAnalysis] Analysis complete');
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[useAnalysis] Error:', apiError.message);

      if (apiError.status === 429) {
        setError('AI token limit reached. Please try again later.');
      } else if (apiError.status === 401) {
        setError('Authentication failed. Check API key configuration.');
      } else if (apiError.status === 503) {
        setError('AI service not configured. Contact administrator.');
      } else {
        setError(apiError.message || 'Failed to analyze record');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset analysis state
   */
  const reset = useCallback(() => {
    setAnalysis(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    analysis,
    loading,
    error,
    analyze,
    reset,
  };
}
